import {
  ActionIcon,
  Alert,
  Badge,
  Card,
  Group,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconTrash } from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

// REST-hook subscriptions created via the public API (Zapier / Make / Pabbly).
// Read-only list with a per-row revoke — distinct from the single UI-managed
// outgoing endpoint on the "Outgoing webhooks" tab.
export function ConnectedAppsPanel() {
  const utils = trpc.useUtils();
  const subs = trpc.tenancy.outgoingWebhook.subscriptions.useQuery();
  const revoke = trpc.tenancy.outgoingWebhook.revokeSubscription.useMutation({
    onSuccess: () => {
      void utils.tenancy.outgoingWebhook.subscriptions.invalidate();
      notifications.show({ message: 'Connection revoked', color: 'gray' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const rows = subs.data ?? [];

  return (
    <Stack gap="md">
      <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
        Apps that connected through the public API (Zapier, Make, Pabbly, or your own integration)
        register their own delivery URLs here. Revoke one to stop sending events to it.
      </Alert>
      {subs.isPending ? (
        <Skeleton height={120} />
      ) : rows.length === 0 ? (
        <Card>
          <Text c="dimmed" ta="center" py="md">
            No connected apps yet. They appear here after subscribing via the API.
          </Text>
        </Card>
      ) : (
        <Stack gap="xs">
          {rows.map((s) => (
            <Card key={s.uid} withBorder>
              <Group justify="space-between" wrap="nowrap">
                <Stack gap={2}>
                  <Group gap="xs">
                    <Badge variant="light" tt="capitalize">
                      {s.source}
                    </Badge>
                    <Badge variant="light" color={s.status === 'active' ? 'green' : 'gray'}>
                      {s.status}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {new Date(s.createdAt).toLocaleString()}
                    </Text>
                  </Group>
                  <Text size="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>
                    {s.targetUrl}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {s.events.length > 0 ? s.events.join(', ') : 'all events'}
                  </Text>
                </Stack>
                <Tooltip label="Revoke">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    loading={revoke.isPending && revoke.variables?.uid === s.uid}
                    onClick={() => revoke.mutate({ uid: s.uid })}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
